/**
 * SAMBA configuration API types
 */

import {
    type AllowedWebsite,
    MerchantConfigSchema,
    type MerchantConfig,
    type MerchantConfigStorage,
    RoleAssignmentCustomConfigSchema,
    type RoleAssignmentCustomConfig,
    type UserRoleAssignmentConfigPatch,
} from './backend_config.types';
import type { InternalRole } from '@pillar-portal/database/dist/frontend';

export type SambaInternalRole = InternalRole;

export interface SambaAutoupdateConfig {
    sambaInstallerVersion: string;
    forceAutoupdate: boolean;
}

export interface EffectiveConfigResponse extends MerchantConfig {
    sambaAutoupdate: SambaAutoupdateConfig;
    internalRole?: SambaInternalRole;
}

export interface NavigateMenuWebsitesResponse {
    websites: AllowedWebsite[];
}

/**
 * Update config request
 */
export interface UpdateConfigRequest {
    config: MerchantConfigStorage;
}

export interface UpdateRoleAssignmentConfigRequest {
    config: RoleAssignmentCustomConfig;
}

export interface PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest {
    userRoleAssignmentConfig: UserRoleAssignmentConfigPatch;
}

/**
 * Combined config response
 */
export interface CombinedConfigResponse {
    user_config: RoleAssignmentCustomConfig;
    org_config: MerchantConfig;
}

/**
 * Role assignment config response
 * Used when getting/updating config for a specific role assignment
 */
export interface RoleAssignmentConfigResponse {
    role_assignment_id: number;
    user_config: RoleAssignmentCustomConfig;
}

/**
 * Get effective config response
 */
export interface GetEffectiveConfigResponse {
    config: MerchantConfig;
    version: string;
}

/**
 * Merge org_config and user_config
 * User config takes precedence over org config
 */
export function mergeConfigs(
    orgConfig?: MerchantConfig,
    userConfig?: RoleAssignmentCustomConfig
): MerchantConfig {
    const organizationConfig: MerchantConfig = MerchantConfigSchema.parse(orgConfig ?? {});
    const roleAssignmentConfig: RoleAssignmentCustomConfig =
        RoleAssignmentCustomConfigSchema.parse(userConfig ?? {});
    const mergedConfigValue: MergeableConfigRecord = deepMergeConfigObjects(
        organizationConfig,
        roleAssignmentConfig
    );
    return MerchantConfigSchema.parse(mergedConfigValue);
}

type MergeableConfigPrimitive = boolean | number | string | null | undefined;

type MergeableConfigObject = object;

type MergeableConfigValue =
    | MergeableConfigPrimitive
    | MergeableConfigObject
    | ReadonlyArray<MergeableConfigValue>;

interface MergeableConfigRecord {
    [key: string]: MergeableConfigValue;
}

function isMergeableObject(value: MergeableConfigValue): value is MergeableConfigObject {
    return (
        value !== null &&
        value !== undefined &&
        !Array.isArray(value) &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
}

function toMergeableConfigRecord(value: MergeableConfigObject): MergeableConfigRecord {
    // Safe bridge: this helper is only called for MerchantConfig and
    // RoleAssignmentCustomConfig plain-object branches validated by
    // Zod and recursively composed from the same config domain.
    // eslint-disable-next-line no-restricted-syntax
    return value as MergeableConfigRecord;
}

function deepMergeConfigObjects(
    baseObject: MergeableConfigObject,
    overrideObject: MergeableConfigObject
): MergeableConfigRecord {
    const baseRecord: MergeableConfigRecord = toMergeableConfigRecord(baseObject);
    const overrideRecord: MergeableConfigRecord = toMergeableConfigRecord(overrideObject);
    const mergedRecord: MergeableConfigRecord = { ...baseRecord };

    for (const overrideKey of Object.keys(overrideRecord)) {
        const overrideEntryValue: MergeableConfigValue = overrideRecord[overrideKey];
        const mergedEntryValue: MergeableConfigValue = deepMergeConfigValues(
            baseRecord[overrideKey],
            overrideEntryValue
        );

        if (mergedEntryValue !== undefined) {
            mergedRecord[overrideKey] = mergedEntryValue;
        }
    }

    return mergedRecord;
}

function deepMergeConfigValues(
    baseValue: MergeableConfigValue,
    overrideValue: MergeableConfigValue
): MergeableConfigValue {
    if (overrideValue === undefined) {
        return baseValue;
    }

    if (Array.isArray(baseValue) && Array.isArray(overrideValue)) {
        return [...baseValue, ...overrideValue];
    }

    if (Array.isArray(overrideValue)) {
        return [...overrideValue];
    }

    if (isMergeableObject(baseValue) && isMergeableObject(overrideValue)) {
        return deepMergeConfigObjects(baseValue, overrideValue);
    }

    return overrideValue;
}
